import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { TopBar } from './TopBar'
import { SideNav } from './SideNav'
import { fetchMe } from '../lib/api'
import { personOf, SessionContext } from './live/session'
import type { SessionState } from './live/session'
import { planPresentation } from './plan'
import './app-shell.css'

export interface AppShellProps {
  /** Breadcrumb tail — the current page, shown after the workspace name. */
  page: string
  /**
   * A section within the page that has its own address (Settings' tabs). Named in the title, the
   * heading a screen reader lands on and the breadcrumb, because focus moves to the content on
   * every navigation and "Settings" alone said nothing about which tab had loaded.
   */
  subpage?: string
  children?: ReactNode
}

/**
 * The application shell: app bar, breadcrumb, sidebar, and a white card holding the page.
 *
 * A light Google Cloud Console layout. Grey ground, white content card, no dark surface
 * anywhere — which replaces the navy sidebar the handoff on disk still describes.
 *
 * The workspace name comes from the session and nowhere else. It used to come from a fixture
 * list of two invented companies, which was harmless while the pages below were fixtures too and
 * became the worst kind of bug the moment they were not: a real tenant's real findings, captioned
 * with a company that does not exist. Nothing in the browser gets to name the tenant — the server
 * derives it from the session, the same way every read below derives its scope.
 *
 * The paywall is a rounded banner ABOVE the card rather than a full-width strip across the top
 * of the window. That keeps it inside the content column, where it reads as a note about this
 * workspace rather than as chrome belonging to the product.
 */
/**
 * The width below which the rail stops being a rail and stacks ABOVE the content (app-shell.css,
 * SideNav's FLYOUT_MIN_WIDTH). There, an open navigation is a full screen of links sitting between
 * a person and the page they asked for -- on a phone the dashboard started below the fold.
 */
const STACKED_NAV = '(max-width: 900px)'

function navIsStacked(): boolean {
  try {
    return window.matchMedia(STACKED_NAV).matches
  } catch {
    return false
  }
}

export function AppShell({ page, subpage, children }: AppShellProps) {
  const [session, setSession] = useState<SessionState>({ state: 'loading' })
  // Open beside the content on a wide screen; closed (behind the menu button) where it would stack
  // on top of it.
  const [navOpen, setNavOpen] = useState(() => !navIsStacked())
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // Where the navigation stacks above the page, choosing a destination closes it -- otherwise the
  // page someone just asked for opens under the menu they used to ask for it. The shell is reused
  // across routes (same component type), so this follows the path rather than relying on a remount.
  useEffect(() => {
    if (navIsStacked()) setNavOpen(false)
  }, [pathname])

  // A NAVIGATION SHOULD BE PERCEIVABLE. Every page had the same browser title, and moving between
  // them announced nothing and left focus on the link just used, so a screen-reader user could not
  // tell the page had changed. The title names the page (and the workspace, once known); focus moves
  // to the content on every navigation after the first, where the page's own h1 is read first.
  const mainRef = useRef<HTMLElement>(null)
  const firstPath = useRef(true)
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false
      return
    }
    mainRef.current?.focus()
  }, [pathname])

  useEffect(() => {
    let live = true

    fetchMe().then((result) => {
      if (!live) return

      // Three states, not two. "The server said you are nobody" and "we could not ask" look the
      // same to a reader and must not look the same to the pages inside, which compare this
      // against the workspace their own data came from.
      setSession(result.ok ? { state: 'known', me: result.data } : { state: 'unknown' })

      // The server SAID this is nobody (a 401): an expired session, or a bookmarked page opened
      // signed out. Every page below then said "sign in again" with nothing to click, so send them
      // to sign-in. ONLY on that answer -- a transient failure (the 'unknown' branch) must never
      // bounce somebody with a good session to sign-in; that was the sign-in loop Home.tsx ended.
      if (result.ok && result.data === null) navigate('/auth/sign-in', { replace: true })
    })

    return () => {
      live = false
    }
  }, [navigate])

  // Until the session answers, the breadcrumb says nothing rather than guessing. A placeholder
  // name here would be read as the tenant's, and there is no safe placeholder for that.
  const person = personOf(session)
  const workspace = person?.workspaceName ?? null

  useEffect(() => {
    const named = subpage ? `${subpage} · ${page}` : page
    document.title = workspace ? `${named} · ${workspace} — Defenssive` : `${named} — Defenssive`
  }, [page, subpage, workspace])

  // The paywall banner, from the REAL plan. Shown only for a KNOWN workspace whose monitoring is
  // off, using the server's canonical `monitoringActive` (auth.is_on_monitor) for the on/off
  // decision and the shared helper only for the wording. No banner during loading, on a lookup
  // failure, for a signed-out visitor, or during onboarding (no workspace) -- previously it showed
  // on every workspace, paid ones included, because the flag defaulted to "free" and was never set.
  const bannerText =
    person?.workspaceId != null && person.monitoringActive === false
      ? planPresentation(person.plan, person.planStatus).banner
      : null

  return (
    <div className="app-shell">
      <TopBar
        workspace={workspace}
        loading={session.state === 'loading'}
        person={person}
        onToggleNav={() => setNavOpen((open) => !open)}
        navOpen={navOpen}
      />

      <div className={navOpen ? 'app-body' : 'app-body is-nav-hidden'}>
        {navOpen ? <SideNav assessesAzure={person?.assessesAzure === true} assessesAi={person?.assessesAiPosture === true} /> : null}

        <main className="app-content" ref={mainRef} tabIndex={-1}>
          <h1 className="sr-only">{subpage ? `${page}: ${subpage}` : page}</h1>
          {/*
            Inside the content column, not above the whole page. It used to sit between the app
            bar and the body, which pushed the sidebar down by its height -- so the rule down the
            side of the navigation began halfway to the first link instead of meeting the bar,
            and the panel read as floating in the content area rather than bounding it.

            An ordered list, because a breadcrumb is a hierarchy and not two pieces of text.
          */}
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>{workspace ?? '—'}</li>
              {subpage ? <li>{page}</li> : null}
              <li aria-current="page">{subpage ?? page}</li>
            </ol>
          </nav>

          {/* A NOTE, NOT A CALL TO ACTION, until there is somewhere for the button to go.
              "Pick a plan" was styled as the primary action on every screen and had no onClick,
              so the most prominent control in the product did nothing. A dead primary button is
              worse than no button: it reads as broken rather than as unfinished.

              Now driven by the workspace's REAL plan (Me.monitoringActive, the server's
              auth.is_on_monitor answer) rather than a prop that defaulted to "free" and was never
              set -- so a paying customer is no longer told they are on the free tier, and the
              wording is honest for each off-state (free / past due / canceled). */}
          {bannerText ? (
            <div className="paywall" role="note">
              <span>{bannerText}</span>
            </div>
          ) : null}

          {/* The pages inside compare what the server sent them against this, so a workspace
              name in the chrome and findings in the card can never describe two tenants. */}
          <div className="content-card rise-in">
            <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
          </div>
        </main>
      </div>
    </div>
  )
}
